import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { toast } from "sonner";
import {
  apiLogin,
  apiRegister,
  apiMe,
  apiLogout,
  apiExchangeSession,
} from "../api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await apiMe();
      setUser(me);
      return me;
    } catch (error) {
      // 401 is expected when not logged in; only log unexpected errors
      if (error?.response?.status && error.response.status !== 401) {
        console.error("Auth refresh failed:", error);
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, let AppRouter handle it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const loginEmail = useCallback(async (email, password) => {
    const res = await apiLogin({ email, password });
    setUser(res.user); // session carried by httpOnly cookie
    toast.success(`Welcome back, ${res.user.name}!`);
    return res.user;
  }, []);

  const registerEmail = useCallback(async (name, email, password) => {
    const res = await apiRegister({ name, email, password });
    setUser(res.user); // session carried by httpOnly cookie
    toast.success(`Welcome, ${res.user.name}!`);
    return res.user;
  }, []);

  const exchangeSession = useCallback(async (sessionId) => {
    const res = await apiExchangeSession(sessionId);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    setUser(null);
    toast.success("Logged out");
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      setUser,
      refresh,
      loginEmail,
      registerEmail,
      exchangeSession,
      logout,
    }),
    [user, loading, refresh, loginEmail, registerEmail, exchangeSession, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
